<template>
  <div class="ui-page">
    <ui-page-header title="Hab/Div em Lote" back="/divergencias" back-text="Hab/Div">
      <v-btn v-if="$allowed('event.launch')"
        color="primary"
        depressed
        :loading="dataset.loading"
        @click="save"
      >
        Salvar lote
      </v-btn>
    </ui-page-header>

    <v-form :readonly="!$allowed('event.launch')" ref="form" v-model="dataForm.validForm">
      <ui-section
        title="Caso"
        description="Selecione o caso antes de incluir os pedidos de habilitação/divergência."
      >
        <v-autocomplete
          :disabled="dataset.loading"
          dense
          required
          :items="dataset.clients"
          label="Caso"
          v-model="dataset.client_id"
          item-value="id"
          item-text="name"
          :rules="[$rules.required]"
          outlined
          hide-details="auto"
          no-data-text="Nenhum caso encontrado"
          @change="onClientChange"
        ></v-autocomplete>
      </ui-section>

      <ui-section v-if="dataset.client_id" title="Pedidos do lote">
        <template #actions>
          <input
            ref="bulkImportInput"
            type="file"
            class="d-none"
            accept=".csv,.xlsx,.xls"
            @change="handleImportFile"
          />
          <ui-action-menu label="Planilha" icon="mdi-file-table-outline" :items="[
            { text: 'Baixar modelo', icon: 'mdi-file-download-outline', disabled: dataset.loading || dataset.importing, handler: downloadTemplate },
            { text: 'Importar carga', icon: 'mdi-file-upload-outline', description: 'CSV ou Excel no formato do modelo', loading: dataset.importing, disabled: dataset.loading, handler: openImportFile },
          ]" />
          <v-btn v-if="$allowed('event.launch')" color="primary" depressed @click="addRequest">
            <v-icon left small>mdi-plus</v-icon>
            Adicionar pedido
          </v-btn>
        </template>

        <v-alert v-if="!dataset.requests.length" type="info" text class="mb-6">
          Adicione ao menos um pedido para iniciar o lote.
        </v-alert>

        <v-expansion-panels v-model="panel" multiple>
          <v-expansion-panel
            v-for="(requestItem, requestIndex) in dataset.requests"
            :key="requestItem.localId"
          >
            <v-expansion-panel-header>
              <div class="d-flex align-center justify-space-between pr-4">
                <span>
                  <strong>Pedido #{{ requestIndex + 1 }}</strong>
                  <span class="ml-2">{{ requestItem.user_name || 'Credor não informado' }}</span>
                </span>
                <v-chip small color="primary" outlined>
                  {{ requestItem.items.length }} negócio(s)
                </v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    :disabled="dataset.loading"
                    dense
                    label="Nome completo"
                    v-model="requestItem.user_name"
                    required
                    :rules="[$rules.required]"
                    outlined
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    :disabled="dataset.loading"
                    dense
                    label="CPF/CNPJ"
                    v-model="requestItem.user_registration"
                    required
                    :rules="registrationRules(requestItem.user_registration)"
                    outlined
                    hide-details="auto"
                    @input="formatRegistration(requestItem)"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    :disabled="dataset.loading"
                    dense
                    label="E-mail cadastral (opcional)"
                    v-model="requestItem.user_email"
                    outlined
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    :disabled="dataset.loading"
                    dense
                    :items="dataset.submissionOrigins"
                    label="Origem do pedido"
                    v-model="requestItem.submission_origin"
                    item-value="value"
                    item-text="text"
                    :rules="[$rules.required]"
                    outlined
                    hide-details="auto"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-alert dense text type="info" class="mb-0">
                    Use “Recuperanda” quando a AJ estiver registrando um pedido recebido da devedora por e-mail.
                  </v-alert>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    :disabled="dataset.loading"
                    dense
                    label="Nome do contato do pedido"
                    v-model="requestItem.contact_name"
                    outlined
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :disabled="dataset.loading"
                    dense
                    label="E-mail para notificações do pedido"
                    v-model="requestItem.contact_email"
                    :placeholder="requestItem.user_email"
                    outlined
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>

              <div class="d-flex align-center justify-space-between mb-3">
                <strong>Negócios</strong>
                <v-btn v-if="$allowed('event.launch')" small outlined color="primary" @click="addItem(requestIndex)">
                  <v-icon left small>mdi-plus</v-icon>
                  Adicionar negócio
                </v-btn>
              </div>

              <v-card
                v-for="(item, itemIndex) in requestItem.items"
                :key="item.localId"
                flat
                outlined
                class="mb-4 pa-4"
              >
                <div class="d-flex align-center justify-space-between mb-4">
                  <div class="d-flex align-center flex-wrap">
                    <strong>Negócio #{{ itemIndex + 1 }}</strong>
                    <span
                      v-if="item.collapsed"
                      class="ml-3 text-truncate ui-muted bulk-item-summary"
                    >
                      {{ getItemSummary(item) }}
                    </span>
                  </div>
                  <div class="d-flex align-center">
                    <v-btn
                      icon
                      color="primary"
                      :disabled="dataset.loading"
                      @click="toggleItem(item)"
                    >
                      <v-icon>{{ item.collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      color="error"
                      :disabled="requestItem.items.length === 1 || dataset.loading"
                      @click="removeItem(requestIndex, itemIndex)"
                    >
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </div>

                <v-expand-transition>
                  <div v-show="!item.collapsed">
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          :disabled="dataset.loading"
                          dense
                          label="Origem do negócio"
                          v-model="item.origin"
                          required
                          :rules="[$rules.required]"
                          outlined
                          hide-details="auto"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-select
                          :disabled="dataset.loading"
                          dense
                          :items="dataset.currencyTypes"
                          label="Moeda"
                          v-model="item.currency"
                          item-value="id"
                          item-text="name"
                          :rules="[$rules.required]"
                          outlined
                          hide-details="auto"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-currency-field
                          :disabled="dataset.loading"
                          dense
                          label="Valor"
                          v-model="item.amount"
                          :allowNegative="false"
                          required
                          :rules="[$rules.required]"
                          outlined
                          hide-details="auto"
                        ></v-currency-field>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" md="4">
                        <v-select
                          :disabled="dataset.loading"
                          dense
                          :items="dataset.creditorTypes"
                          label="Classe"
                          v-model="item.type"
                          item-value="id"
                          item-text="name"
                          :rules="[$rules.required]"
                          outlined
                          hide-details="auto"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          :disabled="dataset.loading"
                          dense
                          :items="dataset.clientCompanies"
                          label="Empresa devedora"
                          v-model="item.client_company_id"
                          item-value="id"
                          item-text="name"
                          :rules="[$rules.required]"
                          outlined
                          hide-details="auto"
                          @change="syncItemCompanyName(item)"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-file-input
                          v-model="item.files"
                          :disabled="dataset.loading"
                          dense
                          multiple
                          counter
                          show-size
                          label="Documentos do pedido"
                          placeholder="Selecione os documentos"
                          accept="image/png, image/jpeg, application/pdf, .csv, .xls, .xlsx, application/vnd.ms-excel"
                          :rules="[filesRequired, $rules.filesSize(20)]"
                          outlined
                          hide-details="auto"
                        >
                          <template v-slot:selection="{ text, index }">
                            <v-chip small close color="primary" text-color="white" @click:close="removeFile(item, index)">
                              {{ text }}
                            </v-chip>
                          </template>
                        </v-file-input>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          :disabled="dataset.loading"
                          dense
                          label="Observação"
                          v-model="item.observation"
                          outlined
                          hide-details="auto"
                          rows="2"
                          auto-grow
                        ></v-textarea>
                      </v-col>
                    </v-row>
                  </div>
                </v-expand-transition>
              </v-card>

              <v-btn v-if="$allowed('event.launch')"
                color="error"
                text
                :disabled="dataset.loading"
                @click="removeRequest(requestIndex)"
              >
                <v-icon left>mdi-delete-outline</v-icon>
                Remover pedido
              </v-btn>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </ui-section>
    </v-form>
  </div>
</template>

<script>
import axios from 'axios';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import { v4 as uuidv4 } from 'uuid';

import sanitizers from '@/common/util/string.js';
import currencyTypes from '@/common/data/currency-types.data';
import creditorTypes from '@/common/data/creditor-types.data.js';
import creditorClassUtil from '@/common/util/creditor-class.util';

export default {
  data: () => ({
    panel: [],
    breadcrumbs: [
      { text: 'Homepage', disabled: false, to: '/home', exact: true },
      { text: 'Habilitações/Divergências', disabled: false, to: '/divergencias', exact: true },
      { text: 'Hab/Div em Lote', disabled: true, exact: true },
    ],
    dataset: {
      loading: false,
      importing: false,
      client_id: null,
      clients: [],
      clientCompanies: [],
      requests: [],
      currencyTypes,
      creditorTypes,
      submissionOrigins: [
        { value: 'creditor', text: 'Credor — recebido e registrado pela AJ' },
        { value: 'debtor', text: 'Recuperanda — recebido por e-mail' },
        { value: 'aj', text: 'AJ — análise ou inclusão própria' },
      ],
    },
    dataForm: {
      validForm: true,
    },
  }),

  created() {
    this.loadClients();
  },

  methods: {
    async loadClients() {
      try {
        const result = await axios.get('/api/clients', { params: { limit: 100000 } });
        this.dataset.clients = result.data.content.data || [];
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: 'top-right',
          timeout: 3000,
        });
      }
    },

    async onClientChange() {
      this.dataset.clientCompanies = [];
      this.dataset.creditorTypes = await creditorClassUtil.listByClient(this.dataset.client_id);

      // As linhas ja preenchidas podem apontar para uma classe que nao existe
      // no caso recem-escolhido.
      const availableIds = this.dataset.creditorTypes.map((item) => Number(item.id));

      (this.dataset.requests || []).forEach((request) => {
        (request.items || []).forEach((item) => {
          if (item.type && !availableIds.includes(Number(item.type))) item.type = null;
        });
      });

      if (!this.dataset.client_id) return;

      try {
        const result = await axios.get(`/api/clients/${this.dataset.client_id}`);
        const client = result.data.content || {};
        const companies = Array.isArray(client.companies) ? client.companies : [];
        this.dataset.clientCompanies = companies.length
          ? companies
          : [{ id: "case", name: client.name }];

        if (this.dataset.clientCompanies.length === 1) {
          this.dataset.requests.forEach((requestItem) => {
            requestItem.items.forEach((item) => {
              item.client_company_id = this.dataset.clientCompanies[0].id;
              item.company_name = this.dataset.clientCompanies[0].name;
            });
          });
        }
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: 'top-right',
          timeout: 3000,
        });
      }
    },

    syncItemCompanyName(item) {
      const company = this.dataset.clientCompanies.find(
        (candidate) => String(candidate.id) === String(item.client_company_id)
      );
      item.company_name = company ? company.name : '';
    },

    createItem() {
      return {
        localId: uuidv4(),
        origin: '',
        currency: 1,
        amount: null,
        type: null,
        client_company_id: this.dataset.clientCompanies.length === 1
          ? this.dataset.clientCompanies[0].id
          : null,
        company_name: this.dataset.clientCompanies.length === 1
          ? this.dataset.clientCompanies[0].name
          : '',
        observation: '',
        files: [],
        collapsed: false,
      };
    },

    createItemFromImport(row) {
      const company = this.dataset.clientCompanies.find(
        (candidate) => this.normalizeImportHeader(candidate.name) === this.normalizeImportHeader(row.company_name)
      );
      return {
        ...this.createItem(),
        origin: row.origin || '',
        currency: this.resolveCurrency(row.currency),
        amount: this.parseAmount(row.amount),
        type: this.resolveCreditorType(row.type),
        client_company_id: company ? company.id : null,
        company_name: company ? company.name : row.company_name || '',
        observation: row.observation || '',
        collapsed: true,
      };
    },

    addRequest() {
      this.dataset.requests.push({
        localId: uuidv4(),
        user_name: '',
        user_registration: '',
        user_email: '',
        contact_name: '',
        contact_email: '',
        submission_origin: 'creditor',
        items: [this.createItem()],
      });
      this.panel = [...this.panel, this.dataset.requests.length - 1];
    },

    openImportFile() {
      if (this.$refs.bulkImportInput) {
        this.$refs.bulkImportInput.value = '';
        this.$refs.bulkImportInput.click();
      }
    },

    async handleImportFile(event) {
      const file = event.target.files && event.target.files[0];

      if (!file) return;

      this.dataset.importing = true;

      try {
        const rows = await this.readImportRows(file);
        const requests = this.buildRequestsFromRows(rows);

        if (!requests.length) {
          this.$dialog.notify.error('Nenhum registro válido encontrado na planilha.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        const appendImport = this.dataset.requests.length
          ? await this.$dialog.confirm({
            title: 'Importar carga',
            text: 'Deseja adicionar os registros importados aos pedidos atuais?',
            actions: {
              false: 'Substituir',
              true: 'Adicionar',
            },
          })
          : false;

        if (appendImport) {
          this.dataset.requests = [...this.dataset.requests, ...requests];
        } else {
          this.dataset.requests = requests;
        }

        this.panel = this.dataset.requests.map((_, itemIndex) => itemIndex);

        this.$dialog.notify.success(`${requests.length} pedido(s) importado(s). Inclua os anexos manualmente antes de salvar.`, {
          position: 'top-right',
          timeout: 3500,
        });
      } catch (error) {
        this.$dialog.notify.error(error.message || 'Não foi possível importar a carga.', {
          position: 'top-right',
          timeout: 4000,
        });
      } finally {
        this.dataset.importing = false;
      }
    },

    async readImportRows(file) {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];

      if (!firstSheetName) return [];

      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {
        defval: '',
        raw: false,
      });

      return rows.map((row) => this.normalizeImportRow(row));
    },

    normalizeImportRow(row) {
      const normalizedRow = {};

      Object.keys(row).forEach((key) => {
        normalizedRow[this.normalizeImportHeader(key)] = row[key];
      });

      return {
        user_name: this.pickImportValue(normalizedRow, ['nome_completo', 'nome', 'credor']),
        user_registration: this.pickImportValue(normalizedRow, ['cpf_cnpj', 'cpfcnpj', 'cpf', 'cnpj', 'documento']),
        user_email: this.pickImportValue(normalizedRow, ['email', 'e_mail']),
        contact_name: this.pickImportValue(normalizedRow, ['contato', 'nome_contato', 'nome_do_contato', 'responsavel']),
        contact_email: this.pickImportValue(normalizedRow, ['email_contato', 'email_do_contato', 'email_notificacao', 'email_de_notificacao']),
        submission_origin: this.pickImportValue(normalizedRow, ['origem_do_pedido', 'apresentado_por']),
        company_name: this.pickImportValue(normalizedRow, ['empresa_devedora', 'devedora', 'recuperanda']),
        origin: this.pickImportValue(normalizedRow, ['origem', 'origem_do_negocio', 'processo']),
        currency: this.pickImportValue(normalizedRow, ['moeda']),
        amount: this.pickImportValue(normalizedRow, ['valor', 'valor_do_negocio']),
        type: this.pickImportValue(normalizedRow, ['classe', 'classe_do_credito']),
        observation: this.pickImportValue(normalizedRow, ['observacao', 'observacoes']),
      };
    },

    normalizeImportHeader(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    },

    pickImportValue(row, keys) {
      const key = keys.find((item) => row[item] !== undefined && row[item] !== null && String(row[item]).trim() !== '');
      return key ? String(row[key]).trim() : '';
    },

    buildRequestsFromRows(rows) {
      const requestMap = new Map();

      rows.forEach((row, index) => {
        const registration = sanitizers.sanitizeCpfCnpj(row.user_registration || '');

        if (!row.user_name || !registration) return;

        const key = registration || `${row.user_name}-${index}`;

        if (!requestMap.has(key)) {
          requestMap.set(key, {
            localId: uuidv4(),
            user_name: row.user_name,
            user_registration: registration.length > 11 ? cnpj.format(registration) : cpf.format(registration),
            user_email: row.user_email,
            contact_name: row.contact_name,
            contact_email: row.contact_email,
            submission_origin: ['creditor', 'debtor', 'aj'].includes(String(row.submission_origin).toLowerCase())
              ? String(row.submission_origin).toLowerCase()
              : 'creditor',
            items: [],
          });
        }

        requestMap.get(key).items.push(this.createItemFromImport(row));
      });

      return Array.from(requestMap.values()).filter((requestItem) => requestItem.items.length);
    },

    resolveCurrency(value) {
      const normalizedValue = this.normalizeImportHeader(value);

      if (['2', 'usd', 'dolar', 'dollar'].includes(normalizedValue)) return 2;
      if (['3', 'eur', 'euro'].includes(normalizedValue)) return 3;

      return 1;
    },

    resolveCreditorType(value) {
      const normalizedValue = this.normalizeImportHeader(value);

      if (!normalizedValue) return null;

      const aliases = [
        { id: 1, exact: ['1', 'i'], partial: ['classe_i', 'trabalhista'] },
        { id: 2, exact: ['2', 'ii'], partial: ['classe_ii', 'garantia_real'] },
        { id: 3, exact: ['3', 'iii'], partial: ['classe_iii', 'quirografario', 'quirografarios'] },
        { id: 4, exact: ['4', 'iv'], partial: ['classe_iv', 'me_epp', 'me', 'epp'] },
        { id: 5, exact: ['5'], partial: ['extraconcursal', 'extraconcursais'] },
      ];
      const alias = aliases.find((item) => (
        item.exact.includes(normalizedValue)
        || item.partial.some((term) => normalizedValue.includes(term))
      ));

      if (alias) return alias.id;

      const numberMatch = normalizedValue.match(/\d+/);

      if (numberMatch) {
        const typeId = Number(numberMatch[0]);
        if (this.dataset.creditorTypes.some((item) => Number(item.id) === typeId)) return typeId;
      }

      const foundType = this.dataset.creditorTypes.find((item) => {
        const normalizedName = this.normalizeImportHeader(item.name);
        return normalizedName.includes(normalizedValue) || normalizedValue.includes(normalizedName);
      });

      return foundType ? foundType.id : null;
    },

    parseAmount(value) {
      if (value === undefined || value === null || value === '') return null;
      if (typeof value === 'number') return value;

      const normalizedValue = String(value)
        .replace(/[^\d,.-]/g, '')
        .trim();

      if (!normalizedValue) return null;

      if (normalizedValue.includes(',') && normalizedValue.includes('.')) {
        return Number(normalizedValue.replace(/\./g, '').replace(',', '.'));
      }

      if (normalizedValue.includes(',')) {
        return Number(normalizedValue.replace(',', '.'));
      }

      return Number(normalizedValue);
    },

    async downloadTemplate() {
      const XLSX = await import('xlsx');
      const rows = [
        {
          'Nome completo': 'Credor Exemplo',
          'CPF/CNPJ': '000.000.000-00',
          'E-mail': 'credor@exemplo.com',
          'Origem do negócio': 'Processo ou origem do crédito',
          'Origem do pedido': 'creditor',
          'Empresa devedora': this.dataset.clientCompanies[0]
            ? this.dataset.clientCompanies[0].name
            : '',
          Moeda: 'BRL',
          Valor: '1500,00',
          Classe: 'Classe I - Trabalhista',
          Observação: 'Observação opcional',
        },
      ];
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, 'HabDiv');

      const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([output], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');

      link.href = URL.createObjectURL(blob);
      link.download = 'modelo-hab-div-lote.xlsx';
      link.click();
      URL.revokeObjectURL(link.href);
    },

    removeRequest(index) {
      this.dataset.requests.splice(index, 1);
      this.panel = this.dataset.requests.map((_, itemIndex) => itemIndex);
    },

    addItem(requestIndex) {
      this.dataset.requests[requestIndex].items.push(this.createItem());
    },

    removeItem(requestIndex, itemIndex) {
      this.dataset.requests[requestIndex].items.splice(itemIndex, 1);
    },

    toggleItem(item) {
      item.collapsed = !item.collapsed;
    },

    getItemSummary(item) {
      const parts = [
        item.origin || 'Origem não informada',
        this.getCreditorTypeName(item.type),
        item.amount ? this.formatAmount(item.amount, item.currency) : 'Valor não informado',
        `${item.files && item.files.length ? item.files.length : 0} arquivo(s)`,
      ];

      return parts.filter(Boolean).join(' · ');
    },

    getCreditorTypeName(type) {
      const creditorType = this.dataset.creditorTypes.find((item) => Number(item.id) === Number(type));
      return creditorType ? creditorType.name : 'Classe não informada';
    },

    formatAmount(amount, currencyType) {
      const numberAmount = Number(amount);

      if (Number.isNaN(numberAmount)) return amount;

      const currency = this.dataset.currencyTypes.find((item) => Number(item.id) === Number(currencyType));
      const currencyLabel = currency ? currency.currencyFormat : '';
      const formattedAmount = numberAmount.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      return `${currencyLabel} ${formattedAmount}`.trim();
    },

    removeFile(item, index) {
      item.files.splice(index, 1);
    },

    registrationRules(value) {
      const digits = sanitizers.sanitizeCpfCnpj(value || '');
      const validator = digits.length <= 11 ? this.$rules.cpf : this.$rules.cnpj;
      return [this.$rules.required, validator];
    },

    formatRegistration(requestItem) {
      const digits = sanitizers.sanitizeCpfCnpj(requestItem.user_registration || '');
      requestItem.user_registration = digits.length > 11 ? cnpj.format(digits) : cpf.format(digits);
    },

    filesRequired(files) {
      return files && files.length > 0 ? true : 'Adicione ao menos um documento';
    },

    validateRequests() {
      if (!this.dataset.client_id) {
        this.$dialog.notify.error('Selecione um caso antes de continuar', {
          position: 'top-right',
          timeout: 2500,
        });
        return false;
      }

      if (!this.dataset.requests.length) {
        this.$dialog.notify.error('Adicione ao menos um pedido ao lote', {
          position: 'top-right',
          timeout: 2500,
        });
        return false;
      }

      return true;
    },

    buildPayload() {
      const payload = new FormData();

      payload.append('client_id', this.dataset.client_id);

      this.dataset.requests.forEach((requestItem, requestIndex) => {
        payload.append(`requests[${requestIndex}][user_name]`, requestItem.user_name);
        payload.append(
          `requests[${requestIndex}][user_registration]`,
          sanitizers.sanitizeCpfCnpj(requestItem.user_registration)
        );

        if (requestItem.user_email) {
          payload.append(`requests[${requestIndex}][user_email]`, requestItem.user_email);
        }
        payload.append(`requests[${requestIndex}][contact_name]`, requestItem.contact_name || requestItem.user_name);
        payload.append(`requests[${requestIndex}][contact_email]`, requestItem.contact_email || requestItem.user_email || '');
        payload.append(`requests[${requestIndex}][contact_registration]`, sanitizers.sanitizeCpfCnpj(requestItem.user_registration));
        payload.append(`requests[${requestIndex}][submission_origin]`, requestItem.submission_origin);

        requestItem.items.forEach((item, itemIndex) => {
          payload.append(`requests[${requestIndex}][items][${itemIndex}][currency]`, item.currency);
          payload.append(`requests[${requestIndex}][items][${itemIndex}][origin]`, item.origin);
          payload.append(`requests[${requestIndex}][items][${itemIndex}][amount]`, item.amount);
          payload.append(`requests[${requestIndex}][items][${itemIndex}][type]`, item.type);
          if (item.client_company_id) {
            payload.append(`requests[${requestIndex}][items][${itemIndex}][client_company_id]`, item.client_company_id);
          }
          payload.append(`requests[${requestIndex}][items][${itemIndex}][company_name]`, item.company_name || '');
          payload.append(`requests[${requestIndex}][items][${itemIndex}][observation]`, item.observation || '');

          item.files.forEach((file) => {
            const uuid = uuidv4();
            const fileName = `${uuid}_3_${file.name}`;
            payload.append(`requests[${requestIndex}][items][${itemIndex}][files]`, file, fileName);
          });
        });
      });

      return payload;
    },

    async save() {
      if (!this.validateRequests() || !this.$refs.form.validate()) {
        this.$dialog.notify.error('Preencha as informações obrigatórias antes de continuar', {
          position: 'top-right',
          timeout: 2500,
        });
        return;
      }

      const proceedAction = await this.$dialog.confirm({
        text: 'Deseja criar as habilitações/divergências deste lote?',
        title: 'Finalizar lote',
        actions: {
          false: 'Cancelar',
          true: 'Concluir',
        },
      });

      if (!proceedAction) return;

      this.dataset.loading = true;

      try {
        const result = await axios.post('/api/admin/user-events/bulk-request', this.buildPayload());
        const total = result.data.content && result.data.content.created ? result.data.content.created : this.dataset.requests.length;

        await this.$dialog.confirm({
          text: `${total} pedido(s) criado(s) com sucesso.`,
          title: 'Sucesso',
          actions: { true: 'OK' },
        });

        this.$router.replace('/divergencias');
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: 'top-right',
          timeout: 4000,
        });
      } finally {
        this.dataset.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.bulk-item-summary {
  display: inline-block;
  font-size: 13px;
  max-width: min(720px, 60vw);
}
</style>
